<script setup lang="ts" generic="Value extends PickerValue = Dayjs">
import { useControlId } from '../../composables/useControlId'
import { computed, nextTick, ref, shallowRef, watch } from 'vue'
import dayjs, { type Dayjs } from 'dayjs'
import { Calendar, Clock, XCircle } from '@lucide/vue'
import CalendarPanel from './CalendarPanel.vue'
import TimePanel from './TimePanel.vue'
import RrButton from '../button/RrButton.vue'
import RenderContent from '../internal/RenderContent'
import { useFormControl } from '../../composables/useFormControl'
import { useBoundValue } from '../../composables/useBoundValue'
import { usePopup } from '../../composables/usePopup'
import { displayPickerValue, parsePickerInput, readPickerValue, writePickerValue, type PickerValue } from './dateValue'
import { isTimeDisabled, type TimePanelOptions } from './time'
import type { DatePickerProps } from './types'
import './picker.css'

const props = withDefaults(defineProps<DatePickerProps<Value> & { timeOnly?: boolean; timeOptions?: TimePanelOptions }>(), { disabled: undefined, inputReadOnly: false, allowClear: true, open: undefined, defaultOpen: false, placement: 'bottomLeft', picker: 'date', showTime: false, showToday: true, showNow: true, timeOnly: false })
const emit = defineEmits<{
  'update:value': [value: Value | null]
  'update:open': [open: boolean]
  change: [value: Value | null, dateString: string]
  openChange: [open: boolean]
  panelChange: [value: Dayjs, mode: 'date' | 'month' | 'year']
  ok: [value: Value | null]
  focus: [event: FocusEvent]
  blur: [event: FocusEvent]
}>()
defineOptions({ inheritAttrs: false })
const { attrs, config, disabled, size, status, controlAttrs, onFieldChange, onFieldBlur } = useFormControl(props)
const innerValue = shallowRef<Value | null>(props.defaultValue ?? null)
const boundValue = useBoundValue(props, 'value', innerValue)
const selected = computed(() => readPickerValue(boundValue.value, props.valueFormat))
const innerOpen = ref(props.defaultOpen)
const boundOpen = useBoundValue(props, 'open', innerOpen)
const isOpen = computed(() => !disabled.value && Boolean(boundOpen.value))
const withTime = computed(() => props.timeOnly || Boolean(props.showTime))
const format = computed(() => props.format ?? (props.timeOnly ? 'HH:mm:ss' : props.picker === 'month' ? 'YYYY-MM' : props.picker === 'year' ? 'YYYY' : withTime.value ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD'))
const displayValue = computed(() => displayPickerValue(selected.value, format.value))
const inputText = ref(displayValue.value)
const draft = shallowRef<Dayjs>(selected.value ?? props.defaultPickerValue ?? dayjs())
const viewDate = shallowRef(draft.value.startOf('month'))
const wrapperRef = ref<HTMLElement | null>(null)
const inputRef = ref<HTMLInputElement | null>(null)
const panelRef = ref<HTMLElement | null>(null)
const calendarRef = ref<{ focus: () => void } | null>(null)
const popupId = `rr-picker-${useControlId()}`
const invalidInput = ref(false)
const timeOptions = computed(() => ({ ...(typeof props.showTime === 'object' ? props.showTime : {}), ...props.timeOptions, ...props.disabledTime?.(draft.value) }))
const draftDisabled = computed(() => Boolean(props.disabledDate?.(draft.value) || (withTime.value && isTimeDisabled(draft.value, timeOptions.value))))
function resetDraft() {
  const time = typeof props.showTime === 'object' ? props.showTime.defaultValue : undefined
  draft.value = selected.value ?? props.defaultPickerValue ?? time ?? dayjs()
  viewDate.value = draft.value.startOf('month')
  inputText.value = displayValue.value
  invalidInput.value = false
  inputRef.value?.setCustomValidity('')
}
watch(boundValue, () => { resetDraft() })
watch(isOpen, open => { if (open || !invalidInput.value) resetDraft() })
function setOpen(open: boolean) {
  if ((disabled.value && open) || open === Boolean(boundOpen.value)) return
  innerOpen.value = open
  emit('update:open', open)
  emit('openChange', open)
}
const { container, style: popupPosition } = usePopup(isOpen, wrapperRef, panelRef, { placement: () => props.placement, getPopupContainer: () => props.getPopupContainer, close: () => setOpen(false), onEscape: () => inputRef.value?.focus({ preventScroll: true }) })
function publish(value: Dayjs | null) {
  const next = value ? writePickerValue(value, props.valueFormat) as Value : null
  innerValue.value = next
  inputText.value = displayPickerValue(value, format.value)
  invalidInput.value = false
  inputRef.value?.setCustomValidity('')
  emit('update:value', next)
  emit('change', next, inputText.value)
  onFieldChange()
  return next
}
function pickDate(value: Dayjs) {
  if (disabled.value || props.disabledDate?.(value)) return
  if (withTime.value) {
    draft.value = value.hour(draft.value.hour()).minute(draft.value.minute()).second(draft.value.second())
    inputText.value = displayPickerValue(draft.value, format.value)
  } else { publish(value); setOpen(false); inputRef.value?.focus({ preventScroll: true }) }
}
function pickTime(value: Dayjs) { draft.value = value; inputText.value = displayPickerValue(value, format.value) }
function confirm() {
  if (disabled.value || draftDisabled.value) return
  const next = publish(draft.value)
  emit('ok', next)
  setOpen(false)
  inputRef.value?.focus({ preventScroll: true })
}
function clear(event: MouseEvent) { event.stopPropagation(); if (!disabled.value) { publish(null); setOpen(false); inputRef.value?.focus() } }
function commitInput(): boolean {
  if (disabled.value || props.inputReadOnly) return false
  if (inputText.value === displayValue.value) return true
  if (!inputText.value.trim() && props.allowClear) { publish(null); return true }
  const parsed = parsePickerInput(inputText.value, format.value)
  if (!parsed || props.disabledDate?.(parsed) || (withTime.value && isTimeDisabled(parsed, { ...timeOptions.value, ...props.disabledTime?.(parsed) }))) {
    invalidInput.value = true
    inputRef.value?.setCustomValidity('请输入有效且可选的日期或时间')
    return false
  }
  publish(parsed)
  return true
}
async function keydown(event: KeyboardEvent) {
  if (disabled.value || event.isComposing) return
  if (event.key === 'ArrowDown') {
    event.preventDefault(); setOpen(true); await nextTick()
    if (props.timeOnly) panelRef.value?.querySelector<HTMLElement>('[aria-selected="true"]:not([disabled]),[role="option"]:not([disabled])')?.focus()
    else calendarRef.value?.focus()
  } else if (event.key === 'Enter') { event.preventDefault(); if (commitInput()) setOpen(false) }
}
function blur(event: FocusEvent) {
  if (panelRef.value?.contains(event.relatedTarget as Node | null)) return
  commitInput()
  emit('blur', event)
  return onFieldBlur()
}
function now() { draft.value = dayjs(); if (withTime.value) confirm(); else pickDate(draft.value.startOf('day')) }
defineExpose({ focus: (options?: FocusOptions) => inputRef.value?.focus(options), blur: () => inputRef.value?.blur() })
</script>

<template>
  <div ref="wrapperRef" class="rr-picker" :class="[attrs.class, `rr-picker--${size}`, { 'is-open': isOpen, 'is-disabled': disabled, 'is-error': status === 'error' || invalidInput, 'is-warning': status === 'warning' }]" :style="attrs.style as any">
    <slot name="suffixIcon"><Clock v-if="timeOnly" :size="14" class="rr-picker-icon" aria-hidden="true" /><Calendar v-else :size="14" class="rr-picker-icon" aria-hidden="true" /></slot>
    <input ref="inputRef" v-bind="controlAttrs()" v-model="inputText" class="rr-picker-input" type="text" :readonly="inputReadOnly" :disabled="disabled" :placeholder="placeholder ?? (timeOnly ? config.locale?.TimePicker?.placeholder ?? '请选择时间' : config.locale?.DatePicker?.lang?.placeholder ?? '请选择日期')" role="combobox" :aria-expanded="isOpen" :aria-controls="popupId" aria-haspopup="dialog" :aria-invalid="invalidInput || status === 'error' || undefined" @click="setOpen(true)" @keydown="keydown" @focus="emit('focus', $event)" @blur="blur" />
    <button v-if="allowClear && boundValue && !disabled" type="button" class="rr-picker-clear" aria-label="清除日期或时间" @click="clear"><XCircle :size="14" aria-hidden="true" /></button>
  </div>
  <Teleport v-if="container" :to="container">
    <Transition name="rr-picker"><div v-if="isOpen" :id="popupId" ref="panelRef" class="rr-picker-popup" :class="popupClassName" :style="[popupStyle, popupPosition]" role="dialog" aria-label="选择日期或时间" tabindex="-1">
      <div class="rr-picker-panels">
        <CalendarPanel v-if="!timeOnly" ref="calendarRef" v-model:view-date="viewDate" :value="withTime ? draft : selected" :picker="picker" :disabled-date="disabledDate" @select="pickDate" @panel-change="(value, mode) => emit('panelChange', value, mode)"><template v-if="$slots.dateRender" #dateRender="slotProps"><slot name="dateRender" v-bind="slotProps" /></template></CalendarPanel>
        <TimePanel v-if="withTime" v-bind="timeOptions" :value="draft" :format="timeOptions.format ?? (timeOnly && typeof format === 'string' ? format : 'HH:mm:ss')" @update:value="pickTime" />
      </div>
      <div v-if="presets?.length" class="rr-picker-presets"><button v-for="(preset, index) in presets" :key="index" type="button" @click="pickDate(typeof preset.value === 'function' ? preset.value() : preset.value)"><RenderContent :value="preset.label" /></button></div>
      <div v-if="$slots.renderExtraFooter" class="rr-picker-footer"><slot name="renderExtraFooter" :mode="picker" /></div>
      <footer v-if="showToday || withTime" class="rr-picker-footer"><button v-if="withTime ? showNow : showToday" type="button" :disabled="disabledDate?.(dayjs())" @click="now">{{ withTime ? config.locale?.DatePicker?.lang?.now ?? '此刻' : config.locale?.DatePicker?.lang?.today ?? '今天' }}</button><RrButton v-if="withTime" size="small" type="primary" :disabled="draftDisabled" @click="confirm">{{ config.locale?.DatePicker?.lang?.ok ?? '确定' }}</RrButton></footer>
    </div></Transition>
  </Teleport>
</template>
